/**
 * 卡片组件
 * 用于展示内容的容器组件
 */

import React from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import type { BaseComponentProps } from '../../types';

interface CardProps extends BaseComponentProps {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  hover?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
}

const Card: React.FC<CardProps> = ({
  title,
  subtitle,
  actions,
  children,
  className,
  hover = false,
  padding = 'md',
  shadow = 'sm'
}) => {
  const baseClasses = 'bg-white rounded-xl border border-gray-200 overflow-hidden';
  
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8'
  };

  const shadowClasses = {
    none: '',
    sm: 'shadow-soft',
    md: 'shadow-medium',
    lg: 'shadow-strong'
  };

  const cardVariants = {
    initial: { scale: 1 },
    hover: { 
      scale: 1.02,
      transition: { duration: 0.2 }
    }
  };

  return (
    <motion.div
      className={clsx(
        baseClasses,
        shadowClasses[shadow],
        hover && 'cursor-pointer',
        className
      )}
      variants={hover ? cardVariants : undefined}
      initial="initial"
      whileHover={hover ? "hover" : undefined}
    >
      {(title || subtitle || actions) && (
        <div className={clsx(
          'flex items-start justify-between',
          padding !== 'none' && 'px-6 pt-6 pb-4'
        )}>
          <div className="flex-1 min-w-0">
            {title && (
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="mt-1 text-sm text-gray-500">
                {subtitle}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex-shrink-0 ml-4">
              {actions}
            </div>
          )}
        </div>
      )}
      
      <div className={clsx(
        paddingClasses[padding],
        (title || subtitle || actions) && padding !== 'none' && 'pt-0'
      )}>
        {children}
      </div>
    </motion.div>
  );
};

export default Card;